import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Package,
  Download,
  X,
  Trash2,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Search,
  Info,
  DollarSign,
  Layers,
  Image as ImageIcon,
  Tag,
  Settings,
  HelpCircle,
  CheckCircle2,
  AlertCircle,
  Save,
  FileText,
  Upload,
  Check,
  Wand2,
  Zap,
  Barcode,
  Percent,
  ScanBarcode,
} from "lucide-react";
import AddSerialsModal from "./components/AddSerialsModal";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import ProductCategoryModal from "./components/ProductCategoryModal";
import {
  buildCategoryHierarchy,
  filterCategories,
} from "@/lib/categoryUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import { useSelector } from "react-redux";
import { all_routes } from "../../Router/all_routes";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import DefaultEditor from "react-simple-wysiwyg";
import MediaLibraryComponent from "../media/MediaLibraryComponent";
import { ReactSelect } from "@/components/ui/react-select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { eventBus } from "../../core/utils/eventBus";
import { cn, isStoreChargingTax } from "@/lib/utils";

const IS_SUPERMARKET = import.meta.env.VITE_APP_PROFILE === "supermarket";

const AddProduct = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  const [productData, setProductData] = useState({
    name: "",
    description: "",
    short_description: "",
    sku: "",
    type: "simple",
    barcode_symbology: "CODE128",
    barcode: "",
    brand_id: "",
    model_id: "",
    unit_id: "",
    cost: 0,
    store_has_diff_cost: false,
    sell_price: 0,
    store_has_diff_sell_price: false,
    sell_price_min: 0,
    store_has_diff_sell_price_min: false,
    sell_price_max: 0,
    store_has_diff_sell_price_max: false,
    alert_quantity: "",
    store_has_diff_alert_quantity: false,
    warranty_timeframe: "No Warranty",
    warranty: 0,
    imei: "",
    condition: "new",
    upc_code: "",
    is_commission: false,
    display_on_pos: true,
    tax_method: "exclusive",
    is_taxable: true,
    image: "",
    images: [],
    is_serialized: false,
    tracking_type: "none",
    manage_inventory: false,
    has_variant: false,
    is_variant: false,
    is_expirable: false,
    expiry_discount_tiers: [],
    status: "active",
    attributes: null,
    categories: [],
  });

  const currentStore = useSelector((state) => state.current_store);
  const currency = currentStore?.default_currency;
  const currencySymbol = currency?.symbol || "";

  const storeChargesTax = isStoreChargingTax(currentStore);
  const storeTaxPercentage = parseFloat(currentStore?.tax_percentage || 0);
  const sellPriceNum = parseFloat(productData.sell_price) || 0;
  const costPriceNum = parseFloat(productData.cost) || 0;
  const effectiveTaxRate = productData.is_taxable && storeChargesTax ? storeTaxPercentage : 0;
  const taxAmount =
    effectiveTaxRate > 0 && sellPriceNum > 0
      ? productData.tax_method === "inclusive"
        ? (sellPriceNum * effectiveTaxRate) / (100 + effectiveTaxRate)
        : (sellPriceNum * effectiveTaxRate) / 100
      : 0;
  const finalPriceWithTax =
    effectiveTaxRate > 0 && sellPriceNum > 0
      ? productData.tax_method === "inclusive"
        ? sellPriceNum
        : sellPriceNum + taxAmount
      : sellPriceNum;

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryModalInitialName, setCategoryModalInitialName] = useState("");

  const [showBrandModal, setShowBrandModal] = useState(false);
  const [brandFormData, setBrandFormData] = useState({
    name: "",
    description: "",
  });

  const [showUnitModal, setShowUnitModal] = useState(false);
  const [unitFormData, setUnitFormData] = useState({
    name: "",
    short_name: "",
    is_active: true,
    is_default: false,
  });

  const [showModelModal, setShowModelModal] = useState(false);
  const [modelFormData, setModelFormData] = useState({
    name: "",
    brand_id: "",
    description: "",
    image: "",
    status: "active",
    attributes: "",
  });

  const barcodeFormats = [
    { value: "CODE128", label: "Code 128" },
    { value: "CODE39", label: "Code 39" },
    { value: "EAN13", label: "EAN-13" },
    { value: "EAN8", label: "EAN-8" },
    { value: "UPC", label: "UPC" },
  ];

  const conditionOptions = [
    { value: "new", label: "Brand New" },
    { value: "used", label: "Pre-Owned/Refurbished" },
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const warrantyOptions = [
    { value: "No Warranty", label: "None" },
    { value: "Days", label: "Days" },
    { value: "Months", label: "Months" },
    { value: "Years", label: "Years" },
    { value: "Lifetime", label: "Lifetime" },
  ];

  const taxMethodOptions = [
    { value: "exclusive", label: "Exclusive (Price + Tax)" },
    { value: "inclusive", label: "Inclusive (Price includes Tax)" },
  ];

  const trackingTypeOptions = [
    { value: "none", label: "None" },
    { value: "standard", label: "Standard / Batch" },
    { value: "serial", label: "Serial Number" },
  ];

  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [categorySearchInput, setCategorySearchInput] = useState("");
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [brandSearchInput, setBrandSearchInput] = useState("");
  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const [brandLabels, setBrandLabels] = useState({});
  const [unitSearchInput, setUnitSearchInput] = useState("");
  const [unitSearchTerm, setUnitSearchTerm] = useState("");
  const [unitLabels, setUnitLabels] = useState({});
  const [modelSearchInput, setModelSearchInput] = useState("");
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [modelLabels, setModelLabels] = useState({});
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [openingStockEnabled, setOpeningStockEnabled] = useState(false);
  const [openingStockData, setOpeningStockData] = useState([]);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [selectedImagesDetails, setSelectedImagesDetails] = useState([]);
  const [activeSection, setActiveSection] = useState("general");
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [markupPercent, setMarkupPercent] = useState("");
  const [useSamePriceForMinMax, setUseSamePriceForMinMax] = useState(true);
  const [serialModal, setSerialModal] = useState({
    isOpen: false,
    rowIndex: null,
    quantity: 0,
    initialSerials: [],
  });

  const openSerialModal = (index) => {
    const row = openingStockData[index];
    const targetQty = parseInt(row.opening_quantity) || 0;
    if (targetQty <= 0) {
      toast.error("Please enter an opening quantity greater than 0 first.");
      return;
    }
    const serials = (row.serial_numbers || "")
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);

    setSerialModal({
      isOpen: true,
      rowIndex: index,
      quantity: targetQty,
      initialSerials: serials,
    });
  };

  const handleSaveSerials = (serials) => {
    if (serialModal.rowIndex === null) return;
    const n = [...openingStockData];
    n[serialModal.rowIndex].serial_numbers = serials.join(", ");
    setOpeningStockData(n);
  };

  const excludeSerials = useMemo(() => {
    return openingStockData.flatMap((row, idx) => {
      if (idx === serialModal.rowIndex) return [];
      return (row.serial_numbers || "")
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
    });
  }, [openingStockData, serialModal.rowIndex]);

  const sections = useMemo(
    () => [
      { id: "general", label: "General Info", icon: Info },
      { id: "organization", label: "Organization", icon: Tag },
      { id: "pricing", label: "Pricing & Tax", icon: DollarSign },
      { id: "inventory", label: "Inventory", icon: Layers },
      { id: "media", label: "Product Media", icon: ImageIcon },
    ],
    [],
  );

  const generalRef = useRef(null);
  const organizationRef = useRef(null);
  const pricingRef = useRef(null);
  const inventoryRef = useRef(null);
  const mediaRef = useRef(null);

  const sectionRefs = useMemo(
    () => ({
      general: generalRef,
      organization: organizationRef,
      pricing: pricingRef,
      inventory: inventoryRef,
      media: mediaRef,
    }),
    [],
  );

  const handleCostChange = (val) => {
    const costNum = parseFloat(val);
    const markupNum = parseFloat(markupPercent);

    let updated = {
      ...productData,
      cost: val,
    };

    if (!isNaN(costNum) && costNum > 0 && !isNaN(markupNum) && markupPercent !== "") {
      const newSell = Number((costNum * (1 + markupNum / 100)).toFixed(2));
      updated.sell_price = newSell;
      if (useSamePriceForMinMax) {
        updated.sell_price_min = newSell;
        updated.sell_price_max = newSell;
      }
    } else if (!isNaN(costNum) && costNum > 0 && productData.sell_price) {
      const sellNum = parseFloat(productData.sell_price);
      if (!isNaN(sellNum)) {
        setMarkupPercent((((sellNum - costNum) / costNum) * 100).toFixed(2));
      }
    }

    setProductData(updated);
  };

  const handleMarkupChange = (val) => {
    setMarkupPercent(val);
    const markupNum = parseFloat(val);
    const costNum = parseFloat(productData.cost);

    if (!isNaN(costNum) && costNum > 0 && !isNaN(markupNum) && val !== "") {
      const newSell = Number((costNum * (1 + markupNum / 100)).toFixed(2));
      setProductData((prev) => ({
        ...prev,
        sell_price: newSell,
        ...(useSamePriceForMinMax
          ? { sell_price_min: newSell, sell_price_max: newSell }
          : {}),
      }));
    }
  };

  const handleSellingPriceChange = (val) => {
    const sellNum = parseFloat(val);
    const costNum = parseFloat(productData.cost);

    if (!isNaN(costNum) && costNum > 0 && !isNaN(sellNum) && val !== "") {
      setMarkupPercent((((sellNum - costNum) / costNum) * 100).toFixed(2));
    } else if (!val || isNaN(sellNum)) {
      setMarkupPercent("");
    }

    setProductData((prev) => ({
      ...prev,
      sell_price: val,
      ...(useSamePriceForMinMax
        ? { sell_price_min: val, sell_price_max: val }
        : {}),
    }));
  };

  const handleToggleSamePriceForMinMax = (checked) => {
    const isChecked = Boolean(checked);
    setUseSamePriceForMinMax(isChecked);
    if (isChecked) {
      setProductData((prev) => ({
        ...prev,
        sell_price_min: prev.sell_price,
        sell_price_max: prev.sell_price,
      }));
    }
  };

  // Queries
  const { data: brands = [], isFetching: isBrandsFetching } = useQuery({
    queryKey: ["brands", brandSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.BRANDS, {
        params: {
          perPage: 15,
          search: brandSearchTerm || undefined,
        },
      });
      return res.data.data;
    },
    enabled: hasPermission("brand read"),
    staleTime: 30000,
  });

  const { data: units = [], isFetching: isUnitsFetching } = useQuery({
    queryKey: ["units", unitSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.UNITS, {
        params: {
          perPage: 15,
          search: unitSearchTerm || undefined,
        },
      });
      return res.data.data;
    },
  });

  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.STORES.STORES);
      return res.data.data;
    },
  });

  const {
    data: rawCategories = [],
    isLoading: isCategoriesLoading,
    isFetching: isCategoriesFetching,
  } = useQuery({
    queryKey: ["categories", "all", categorySearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.CATEGORIES, {
        params: {
          search: categorySearchTerm || undefined,
          perPage: 9999,
        },
      });
      return res.data?.data || res.data || [];
    },
  });

  const categories = useMemo(() => {
    if (Array.isArray(rawCategories)) return rawCategories;
    if (Array.isArray(rawCategories?.data)) return rawCategories.data;
    return [];
  }, [rawCategories]);

  const { data: models = [], isFetching: isModelsFetching } = useQuery({
    queryKey: ["models", productData.brand_id, modelSearchTerm],
    queryFn: async () => {
      if (!productData.brand_id) return [];
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.BRAND_MODELS(productData.brand_id),
        {
          params: {
            perPage: 15,
            search: modelSearchTerm || undefined,
          },
        },
      );
      return res.data.data.filter((model) => model.status === "active");
    },
    enabled: !!productData.brand_id && hasPermission("brand model read"),
  });

  // Mutations
  const createProductMutation = useMutation({
    mutationFn: async (submitData) => {
      const res = await apiClient.post(
        API_ENDPOINTS.INVENTORY.PRODUCTS,
        submitData,
      );
      return res.data.data;
    },
    onSuccess: (createdProduct) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      localStorage.removeItem("draftData");
      toast.success("Product added successfully");
      eventBus.publish("product:created", createdProduct);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to add product");
    },
  });

  const createBrandMutation = useMutation({
    mutationFn: async (brandData) => {
      const res = await apiClient.post(
        API_ENDPOINTS.INVENTORY.BRANDS,
        brandData,
      );
      return res.data.data;
    },
    onSuccess: (newBrand) => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Brand created successfully!");
      setBrandLabels((prev) => ({
        ...prev,
        [String(newBrand.id)]: newBrand.name,
      }));
      setProductData({ ...productData, brand_id: newBrand.id.toString() });
      setShowBrandModal(false);
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "Failed to create brand"),
  });

  const createUnitMutation = useMutation({
    mutationFn: async (unitData) => {
      const res = await apiClient.post(API_ENDPOINTS.INVENTORY.UNITS, unitData);
      return res.data.data;
    },
    onSuccess: (newUnit) => {
      queryClient.invalidateQueries({ queryKey: ["units"] });
      toast.success("Unit created successfully!");
      setUnitLabels((prev) => ({
        ...prev,
        [String(newUnit.id)]: newUnit.name,
      }));
      setProductData({ ...productData, unit_id: newUnit.id.toString() });
      setShowUnitModal(false);
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "Failed to create unit"),
  });

  const createModelMutation = useMutation({
    mutationFn: async (modelData) => {
      const res = await apiClient.post(
        API_ENDPOINTS.INVENTORY.BRAND_MODELS(modelData.brand_id),
        modelData,
      );
      return res.data.data;
    },
    onSuccess: (newModel) => {
      queryClient.invalidateQueries({
        queryKey: ["models", productData.brand_id],
      });
      toast.success("Model created successfully!");
      setModelLabels((prev) => ({
        ...prev,
        [String(newModel.id)]: newModel.name,
      }));
      setProductData({ ...productData, model_id: newModel.id.toString() });
      setShowModelModal(false);
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "Failed to create model"),
  });

  const createOpeningStockMutation = useMutation({
    mutationFn: async (product) => {
      if (!openingStockEnabled || openingStockData.length === 0) return;
      const rowsToProcess = openingStockData.filter(
        (row) => row.store_id && row.opening_quantity > 0,
      );
      if (rowsToProcess.length === 0) return;

      for (const row of rowsToProcess) {
        const payload = {
          store_id: row.store_id,
          notes: row.notes || "Initial stock for new product",
          items: [
            {
              product_id: product.id,
              opening_quantity: parseInt(row.opening_quantity),
              unit_cost:
                row.unit_cost !== undefined &&
                  row.unit_cost !== null &&
                  row.unit_cost !== ""
                  ? parseFloat(row.unit_cost)
                  : productData.cost !== undefined &&
                    productData.cost !== null &&
                    productData.cost !== ""
                    ? parseFloat(productData.cost)
                    : null,
              reorder_level: row.reorder_level
                ? parseInt(row.reorder_level)
                : parseInt(productData.alert_quantity) || null,
              location: row.location || null,
              sku: product.sku || null,
              reason_notes: "Initial stock for new product",
              serial_numbers: row.serial_numbers
                ? row.serial_numbers
                  .split(",")
                  .map((s) => s.trim())
                  .filter((s) => s !== "")
                : null,
              meta: null,
            },
          ],
        };
        await apiClient.post(API_ENDPOINTS.STOCK.OPENING_BALANCE, payload);
      }
    },
    onSuccess: (_, product) => {
      setTimeout(() => {
        if (product?.id) {
          navigate(all_routes.editproduct.replace(":id", product.id));
        } else {
          navigate(all_routes.productlist);
        }
      }, 1000);
    },
    onError: (err, product) => {
      toast.error(err.response?.data?.message || "Failed to add opening stock");
      setTimeout(() => {
        if (product?.id) {
          navigate(all_routes.editproduct.replace(":id", product.id));
        } else {
          navigate(all_routes.productlist);
        }
      }, 1500);
    },
  });

  useEffect(() => {
    const unsubStock = eventBus.subscribe(
      "product:created",
      async (product) => {
        // Handle Multiple Media Assignments
        const imagesToAssign = productData.images || [];
        if (product.id && imagesToAssign.length > 0) {
          for (const media_id of imagesToAssign) {
            try {
              await apiClient.post(API_ENDPOINTS.MEDIA.ASSIGN, {
                media_id: media_id,
                model_type: "Product",
                model_id: product.id,
                collection_name: "product",
                name: product.name,
                description: null,
                custom_properties: [],
              });
            } catch (error) {
              console.error("Error assigning media:", error);
            }
          }
        }

        if (openingStockEnabled && productData.manage_inventory) {
          createOpeningStockMutation.mutate(product);
        } else {
          setTimeout(() => {
            if (product?.id) {
              navigate(all_routes.editproduct.replace(":id", product.id));
            } else {
              navigate(all_routes.productlist);
            }
          }, 1000);
        }
      },
    );
    return () => unsubStock();
  }, [
    openingStockEnabled,
    productData.manage_inventory,
    productData.images, // Updated from productData.image
    productData.name,
    createOpeningStockMutation,
    navigate,
  ]);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = sectionRefs[sectionId].current;
    if (element) {
      const offset = 150; // Accounts for Header + PageHeader padding
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Debounce category search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCategorySearchTerm(categorySearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [categorySearchInput]);

  // Debounce brand search
  useEffect(() => {
    const timer = setTimeout(() => {
      setBrandSearchTerm(brandSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [brandSearchInput]);

  // Debounce unit search
  useEffect(() => {
    const timer = setTimeout(() => {
      setUnitSearchTerm(unitSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [unitSearchInput]);

  // Debounce model search
  useEffect(() => {
    const timer = setTimeout(() => {
      setModelSearchTerm(modelSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [modelSearchInput]);

  useEffect(() => {
    if (brands.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBrandLabels((prev) => {
        const next = { ...prev };
        brands.forEach((b) => {
          next[String(b.id)] = b.name;
        });
        return next;
      });
    }
  }, [brands]);

  useEffect(() => {
    if (units.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUnitLabels((prev) => {
        const next = { ...prev };
        units.forEach((u) => {
          next[String(u.id)] = u.name;
        });
        return next;
      });
    }
  }, [units]);

  useEffect(() => {
    if (models.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setModelLabels((prev) => {
        const next = { ...prev };
        models.forEach((m) => {
          next[String(m.id)] = m.name;
        });
        return next;
      });
    }
  }, [models]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const ref = sectionRefs[section.id].current;
        if (ref) {
          const offsetTop = ref.offsetTop;
          const height = ref.offsetHeight;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + height
          ) {
            setActiveSection(section.id);
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections, sectionRefs]);

  const validateFields = () => {
    if (!productData.name)
      return { isValid: false, message: "Product name is required" };
    if (
      productData.cost === undefined ||
      productData.cost === null ||
      productData.cost === ""
    )
      return { isValid: false, message: "Cost price is required" };
    if (
      productData.sell_price === undefined ||
      productData.sell_price === null ||
      productData.sell_price === ""
    )
      return { isValid: false, message: "Sell price is required" };

    const effectiveMin = useSamePriceForMinMax
      ? productData.sell_price
      : productData.sell_price_min;
    const effectiveMax = useSamePriceForMinMax
      ? productData.sell_price
      : productData.sell_price_max;

    if (effectiveMin === undefined || effectiveMin === "")
      return { isValid: false, message: "Minimum sell price is required" };
    if (effectiveMax === undefined || effectiveMax === "")
      return { isValid: false, message: "Maximum sell price is required" };

    if (productData.manage_inventory) {
      if (
        !productData.tracking_type ||
        !["standard", "serial"].includes(productData.tracking_type)
      ) {
        return {
          isValid: false,
          message:
            "Tracking type must be 'standard' or 'serial' when managing inventory",
        };
      }
    } else if (
      productData.tracking_type &&
      productData.tracking_type !== "none"
    ) {
      return {
        isValid: false,
        message: `Manage inventory must be enabled when tracking type is ${productData.tracking_type}`,
      };
    }

    if (openingStockEnabled && productData.tracking_type === "serial") {
      for (let i = 0; i < openingStockData.length; i++) {
        const row = openingStockData[i];
        const qty = parseInt(row.opening_quantity) || 0;
        if (qty > 0) {
          const serials = (row.serial_numbers || "")
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s !== "");
          if (serials.length !== qty) {
            return {
              isValid: false,
              message: `Number of serial numbers (${serials.length}) must match opening quantity (${qty}) for opening stock at row ${i + 1}`,
            };
          }
        }
      }
    }

    return { isValid: true };
  };

  const handleSubmit = async () => {
    const { isValid, message } = validateFields();
    if (!isValid) {
      toast.error(message);
      return;
    }

    const submitData = {
      ...productData,
      cost: parseFloat(productData.cost) || 0,
      sell_price: parseFloat(productData.sell_price) || 0,
      sell_price_min: useSamePriceForMinMax
        ? parseFloat(productData.sell_price) || 0
        : parseFloat(productData.sell_price_min) || 0,
      sell_price_max: useSamePriceForMinMax
        ? parseFloat(productData.sell_price) || 0
        : parseFloat(productData.sell_price_max) || 0,
      alert_quantity: parseInt(productData.alert_quantity) || 0,
      brand_id: productData.brand_id ? String(productData.brand_id) : null,
      model_id: productData.model_id ? String(productData.model_id) : null,
      unit_id: productData.unit_id ? String(productData.unit_id) : null,
      categories: selectedCategoryIds.length > 0 ? selectedCategoryIds : [],
    };

    createProductMutation.mutate(submitData);
  };

  const hasSelectedDescendant = (cat, selectedIds) => {
    if (selectedIds.includes(cat.id)) return true;
    if (cat.children && cat.children.length > 0) {
      return cat.children.some((child) =>
        hasSelectedDescendant(child, selectedIds),
      );
    }
    return false;
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderCategoryCheckboxes = (categories, level = 0) => {
    return categories.map((category) => {
      const isExpanded = categorySearchTerm
        ? true
        : expandedIds.has(category.id) ||
        hasSelectedDescendant(category, selectedCategoryIds);
      const hasChildren = category.children && category.children.length > 0;

      return (
        <div key={category.id} style={{ marginLeft: `${level * 20}px` }}>
          <div className="flex items-center space-x-2 py-1">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => toggleExpand(category.id)}
                className="p-1 hover:bg-muted rounded text-muted-foreground transition-colors"
                title={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <div className="w-6" />
            )}
            <Checkbox
              id={`cat-${category.id}`}
              checked={selectedCategoryIds.includes(category.id)}
              onCheckedChange={(checked) => {
                if (checked)
                  setSelectedCategoryIds([...selectedCategoryIds, category.id]);
                else
                  setSelectedCategoryIds(
                    selectedCategoryIds.filter((id) => id !== category.id),
                  );
              }}
            />
            <Label htmlFor={`cat-${category.id}`} className="cursor-pointer">
              {category.name}
            </Label>
          </div>
          {hasChildren &&
            isExpanded &&
            renderCategoryCheckboxes(category.children, level + 1)}
        </div>
      );
    });
  };

  const filteredCategoryTree = useMemo(() => {
    return filterCategories(
      buildCategoryHierarchy(categories),
      categorySearchTerm,
    );
  }, [categories, categorySearchTerm]);

  const trimmedCategorySearch = categorySearchInput.trim();

  const hasExactCategoryMatch = useMemo(() => {
    if (!trimmedCategorySearch) return false;
    const term = trimmedCategorySearch.toLowerCase();
    return categories.some(
      (c) => c?.name && c.name.trim().toLowerCase() === term,
    );
  }, [trimmedCategorySearch, categories]);

  const handleOpenCategoryModal = (name = "") => {
    setCategoryModalInitialName(name);
    setShowCategoryModal(true);
  };

  const isCategoriesBusy =
    isCategoriesLoading ||
    (isCategoriesFetching && categories.length === 0);

  return (
    <div className="bg-muted/10 min-h-[calc(100vh-4rem)]">
      {/* Top Header */}
      <div className="sticky top-16 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/products">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-foreground">
                Add New Product
              </h1>
              <p className="text-sm text-muted-foreground">
                Detailed product configuration
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" asChild>
              <Link to="/products">Cancel</Link>
            </Button>
            <Button
              variant="default"
              className="shadow-md"
              onClick={handleSubmit}
              disabled={createProductMutation.isPending}
            >
              {createProductMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save Product
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-8 relative">
        <div className="flex gap-8 items-start">
          {/* Navigation Sidebar */}
          <div className="hidden lg:block w-64 shrink-0 sticky top-32 h-fit">
            <div className="space-y-1">
              <p className="px-3 mb-4 text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
                Sections
              </p>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group",
                    activeSection === section.id
                      ? "bg-primary/10 text-primary shadow-sm"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <section.icon
                    className={cn(
                      "h-4 w-4",
                      activeSection === section.id
                        ? "text-primary"
                        : "text-muted-foreground group-hover:text-foreground",
                    )}
                  />
                  {section.label}
                  {activeSection === section.id && (
                    <div className="ml-auto w-1.5 h-1.5 bg-primary rounded-full shadow-[0_0_8px_rgba(var(--primary),0.6)]" />
                  )}
                </button>
              ))}

              <div className="mt-8 p-4 bg-primary/5 rounded-xl border border-primary/10">
                <div className="flex items-center gap-2 mb-2 text-primary font-semibold text-xs uppercase tracking-wider">
                  <HelpCircle className="h-3.5 w-3.5" />
                  Quick Tips
                </div>
                <ul className="space-y-2 text-[11px] text-muted-foreground leading-relaxed">
                  <li className="flex gap-2">
                    <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
                    Fill in required fields marked with *
                  </li>
                  <li className="flex gap-2">
                    <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
                    Hover over icons next to labels for more info
                  </li>
                  <li className="flex gap-2">
                    <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
                    Enable "Serialized Item" for unique electronics tracking
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Main Content Scrolling Cards */}
          <div className="flex-1 space-y-10 pb-24">
            {/* General Info Section */}
            <section ref={generalRef} id="general">
              <Card className="shadow-lg border-border/60 overflow-hidden bg-card">
                <div className="h-1 bg-primary" />
                <CardHeader className="bg-muted/50 pb-6 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100/10 text-blue-600 dark:text-blue-400 rounded-lg">
                      <Info className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle>Basic Information</CardTitle>
                      <CardDescription>
                        Essential identification details for the product
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-8 space-y-6">
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label className="text-foreground">
                        Product Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        value={productData.name}
                        onChange={(e) =>
                          setProductData({
                            ...productData,
                            name: e.target.value,
                          })
                        }
                        placeholder="e.g. Premium Wireless Headphones"
                        className="bg-muted/30 focus:bg-card h-11 text-lg font-medium"
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label className="text-foreground">
                            SKU
                          </Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              <p className="text-xs">
                                A unique alphanumeric identifier for your
                                product
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Input
                          value={productData.sku}
                          onChange={(e) =>
                            setProductData({
                              ...productData,
                              sku: e.target.value,
                            })
                          }
                          placeholder="PRD-001-BLU"
                          className="bg-muted/30 focus:bg-card"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Condition</Label>
                        <ReactSelect
                          options={conditionOptions}
                          value={conditionOptions.find(
                            (opt) =>
                              opt.value === productData.condition ||
                              (opt.value === "used" &&
                                [
                                  "used",
                                  "pre-owned",
                                  "preowned",
                                  "refurbished",
                                ].includes(
                                  productData.condition?.toLowerCase?.(),
                                )),
                          )}
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              condition: opt.value,
                            })
                          }
                          placeholder="Select condition"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <ReactSelect
                          options={statusOptions}
                          value={statusOptions.find(
                            (opt) => opt.value === productData.status,
                          )}
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              status: opt.value,
                            })
                          }
                          placeholder="Select status"
                        />
                      </div>
                    </div>

                    {/* Short Description & Modal Trigger */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-foreground font-medium">
                          Short Description
                        </Label>
                        <Button
                          type="button"
                          variant="link"
                          className="p-0 h-auto text-xs font-semibold text-primary hover:text-primary/80 flex items-center gap-1.5"
                          onClick={() => setShowDescriptionModal(true)}
                        >
                          <FileText className="h-3.5 w-3.5" />
                          {productData.description
                            ? "Edit Detailed Description"
                            : "+ Add Detailed Description"}
                        </Button>
                      </div>
                      <Textarea
                        rows={3}
                        placeholder="Brief summary or highlight of the product..."
                        value={productData.short_description || ""}
                        onChange={(e) => {
                          if (e.target.value.length <= 255) {
                            setProductData({
                              ...productData,
                              short_description: e.target.value,
                            });
                          }
                        }}
                        className="bg-muted/30 focus:bg-card resize-none"
                      />
                      <div className="flex justify-between items-center text-[11px] text-muted-foreground">
                        <span>Appears in list views, receipts and search results</span>
                        <span>{(productData.short_description || "").length} / 255</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl border border-dashed border-border">
                      <Switch
                        checked={productData.display_on_pos}
                        onCheckedChange={(c) =>
                          setProductData({ ...productData, display_on_pos: c })
                        }
                        id="pos"
                      />
                      <Label htmlFor="pos" className="cursor-pointer">Display on POS</Label>
                    </div>

                    {/* Collapsible Accordion for Barcode, UPC, IMEI, Symbology, Warranty, Commission */}
                    <Accordion
                      type="single"
                      collapsible
                      className="w-full border border-border/70 rounded-xl overflow-hidden bg-muted/10"
                    >
                      <AccordionItem value="identifiers" className="border-none">
                        <AccordionTrigger className="px-5 py-3.5 hover:no-underline font-medium text-sm text-foreground hover:bg-muted/20">
                          <div className="flex items-center gap-2.5">
                            <Barcode className="h-4 w-4 text-primary" />
                            <span>Additional Information</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-5 pb-5 pt-2 space-y-6">
                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <Label>Barcode Number</Label>
                              <Input
                                value={productData.barcode}
                                onChange={(e) =>
                                  setProductData({
                                    ...productData,
                                    barcode: e.target.value,
                                  })
                                }
                                placeholder="Barcode number"
                                className="bg-muted/30 focus:bg-card"
                              />
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Label>Barcode Symbology</Label>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent side="right">
                                    <p className="text-xs">
                                      The format used for scanning and generating
                                      barcodes
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                              <ReactSelect
                                options={barcodeFormats}
                                value={barcodeFormats.find(
                                  (opt) => opt.value === productData.barcode_symbology,
                                )}
                                onChange={(opt) =>
                                  setProductData({
                                    ...productData,
                                    barcode_symbology: opt.value,
                                  })
                                }
                              />
                            </div>
                          </div>

                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <Label>UPC Code</Label>
                              <Input
                                value={productData.upc_code}
                                onChange={(e) =>
                                  setProductData({
                                    ...productData,
                                    upc_code: e.target.value,
                                  })
                                }
                                placeholder="Universal Product Code"
                                className="bg-muted/30 focus:bg-card"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>IMEI</Label>
                              <Input
                                value={productData.imei}
                                onChange={(e) =>
                                  setProductData({
                                    ...productData,
                                    imei: e.target.value,
                                  })
                                }
                                placeholder="IMEI"
                                className="bg-muted/30 focus:bg-card"
                              />
                            </div>
                          </div>

                          <div className="grid md:grid-cols-2 gap-6 items-end">
                            <div className="space-y-2">
                              <Label className="text-foreground">
                                Warranty Coverage
                              </Label>
                              <div className="flex gap-2 p-1.5 bg-muted/30 rounded-lg border border-border">
                                <Input
                                  type="number"
                                  className="w-20 bg-background border-none shadow-none h-8 text-center"
                                  value={productData.warranty}
                                  min="0"
                                  onChange={(e) =>
                                    setProductData({
                                      ...productData,
                                      warranty: Math.max(0, e.target.value),
                                    })
                                  }
                                  placeholder="0"
                                />
                                <div className="w-px h-6 bg-border self-center" />
                                <ReactSelect
                                  options={warrantyOptions}
                                  value={warrantyOptions.find(
                                    (opt) =>
                                      opt.value === productData.warranty_timeframe,
                                  )}
                                  onChange={(opt) =>
                                    setProductData({
                                      ...productData,
                                      warranty_timeframe: opt.value,
                                    })
                                  }
                                  className="flex-1"
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      backgroundColor: "transparent",
                                      border: "none",
                                      boxShadow: "none",
                                      minHeight: "32px",
                                      height: "32px",
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      padding: "0 8px",
                                    }),
                                  }}
                                />
                              </div>
                            </div>

                            <div className="flex items-center justify-between p-2.5 px-4 bg-muted/30 rounded-lg border border-border h-[46px]">
                              <div className="space-y-0.5">
                                <Label htmlFor="comm" className="text-sm font-medium cursor-pointer">
                                  Commission
                                </Label>
                                <p className="text-[11px] text-muted-foreground">
                                  Enable sales commission for this product
                                </p>
                              </div>
                              <Switch
                                checked={productData.is_commission}
                                onCheckedChange={(c) =>
                                  setProductData({ ...productData, is_commission: c })
                                }
                                id="comm"
                              />
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Organization Info Section */}
            <section ref={organizationRef} id="organization">
              <Card className="shadow-lg border-border/60 overflow-hidden bg-card">
                <div className="h-1 bg-primary" />
                <CardHeader className="bg-muted/50 pb-6 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 text-primary rounded-lg">
                      <Tag className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle>Organization & Classification</CardTitle>
                      <CardDescription>
                        Brands, categories and grouping
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-8 space-y-6">
                  <div className="grid lg:grid-cols-2 gap-10">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label className="text-base font-bold">
                            Categories
                          </Label>
                          {isCategoriesBusy && (
                            <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-normal">
                              <Spinner size={12} className="text-primary" />
                              Loading...
                            </span>
                          )}
                        </div>
                        {hasPermission("category product create") && (
                          <Button
                            type="button"
                            variant="link"
                            className="p-0 h-auto font-bold text-primary hover:text-primary/80"
                            onClick={() =>
                              handleOpenCategoryModal(trimmedCategorySearch)
                            }
                          >
                            {trimmedCategorySearch
                              ? `+ Add "${trimmedCategorySearch}"`
                              : "+ New Category"}
                          </Button>
                        )}
                      </div>
                      <div className="relative group">
                        <Input
                          value={categorySearchInput}
                          onChange={(e) =>
                            setCategorySearchInput(e.target.value)
                          }
                          className="pl-9 pr-8 h-11 bg-muted/30 group-focus-within:bg-card"
                          placeholder="Find or select categories..."
                        />
                        {isCategoriesFetching && !isCategoriesLoading ? (
                          <Spinner
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          />
                        ) : (
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        )}
                        {categorySearchInput && (
                          <button
                            type="button"
                            onClick={() => setCategorySearchInput("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 rounded transition-colors"
                            title="Clear search"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="border border-border rounded-2xl p-4 h-[320px] overflow-y-auto bg-muted/5 shadow-inner">
                        {isCategoriesBusy ? (
                          <div className="space-y-3 p-1">
                            <div className="flex items-center justify-between text-xs text-muted-foreground pb-2 border-b border-border/40">
                              <span className="flex items-center gap-1.5 font-medium">
                                <Spinner size={13} className="text-primary" />
                                Loading categories...
                              </span>
                              <span className="text-[11px] text-muted-foreground/70">Please wait</span>
                            </div>
                            <div className="space-y-3 animate-pulse pt-1">
                              <div className="flex items-center gap-2.5">
                                <Skeleton className="h-4 w-4 rounded" />
                                <Skeleton className="h-4 w-32 rounded" />
                              </div>
                              <div className="flex items-center gap-2.5 pl-6">
                                <Skeleton className="h-4 w-4 rounded" />
                                <Skeleton className="h-4 w-24 rounded" />
                              </div>
                              <div className="flex items-center gap-2.5 pl-6">
                                <Skeleton className="h-4 w-4 rounded" />
                                <Skeleton className="h-4 w-36 rounded" />
                              </div>
                              <div className="flex items-center gap-2.5">
                                <Skeleton className="h-4 w-4 rounded" />
                                <Skeleton className="h-4 w-28 rounded" />
                              </div>
                              <div className="flex items-center gap-2.5 pl-6">
                                <Skeleton className="h-4 w-4 rounded" />
                                <Skeleton className="h-4 w-40 rounded" />
                              </div>
                              <div className="flex items-center gap-2.5">
                                <Skeleton className="h-4 w-4 rounded" />
                                <Skeleton className="h-4 w-30 rounded" />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            {hasPermission("category product create") &&
                              trimmedCategorySearch &&
                              !hasExactCategoryMatch &&
                              filteredCategoryTree.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleOpenCategoryModal(trimmedCategorySearch)
                                  }
                                  className="w-full flex items-center justify-between p-2.5 mb-3 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/15 dark:bg-primary/20 dark:hover:bg-primary/25 rounded-xl border border-primary/20 transition-colors shadow-2xs"
                                >
                                  <span className="flex items-center gap-1.5 truncate">
                                    <Plus className="h-3.5 w-3.5 flex-shrink-0" />
                                    Add category &quot;
                                    <span className="font-semibold truncate">
                                      {trimmedCategorySearch}
                                    </span>
                                    &quot;
                                  </span>
                                  <span className="text-[11px] font-semibold underline ml-2 flex-shrink-0">
                                    Create
                                  </span>
                                </button>
                              )}

                            {filteredCategoryTree.length > 0 ? (
                              renderCategoryCheckboxes(filteredCategoryTree)
                            ) : (
                              <div className="h-full flex flex-col items-center justify-center p-4 text-center">
                                <Tag className="h-8 w-8 mb-2 opacity-40 text-muted-foreground" />
                                <p className="text-sm text-muted-foreground mb-3">
                                  {trimmedCategorySearch ? (
                                    <>
                                      No categories found matching &quot;
                                      <span className="font-semibold text-foreground">
                                        {trimmedCategorySearch}
                                      </span>
                                      &quot;
                                    </>
                                  ) : (
                                    "No categories available"
                                  )}
                                </p>
                                {hasPermission("category product create") &&
                                  trimmedCategorySearch && (
                                    <Button
                                      type="button"
                                      size="sm"
                                      className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm"
                                      onClick={() =>
                                        handleOpenCategoryModal(
                                          trimmedCategorySearch,
                                        )
                                      }
                                    >
                                      <Plus className="h-4 w-4 mr-1.5" />
                                      Add &quot;{trimmedCategorySearch}&quot;
                                    </Button>
                                  )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    <div className="space-y-8">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="font-bold">Brand / Make</Label>
                          {hasPermission("brand create") && (
                            <Button
                              variant="link"
                              className="p-0 h-auto text-xs font-bold text-primary hover:text-primary/80"
                              onClick={() => setShowBrandModal(true)}
                            >
                              + New Brand
                            </Button>
                          )}
                        </div>
                        <ReactSelect
                          options={brands.map((b) => ({
                            value: String(b.id),
                            label: b.name,
                          }))}
                          value={
                            productData.brand_id
                              ? {
                                value: String(productData.brand_id),
                                label:
                                  brandLabels[String(productData.brand_id)] ||
                                  brands.find(
                                    (b) =>
                                      String(b.id) ===
                                      String(productData.brand_id),
                                  )?.name ||
                                  "Select Brand",
                              }
                              : null
                          }
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              brand_id: opt?.value ?? "",
                              model_id: "",
                            })
                          }
                          onInputChange={(value) => setBrandSearchInput(value)}
                          isLoading={isBrandsFetching}
                          filterOption={() => true}
                          placeholder="Search brands..."
                          isClearable
                        />
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="font-bold">Model</Label>
                          {hasPermission("brand model create") && (
                            <Button
                              disabled={!productData.brand_id}
                              variant="link"
                              className="p-0 h-auto text-xs font-bold text-primary hover:text-primary/80 disabled:opacity-30"
                              onClick={() => setShowModelModal(true)}
                            >
                              + New Model
                            </Button>
                          )}
                        </div>
                        <ReactSelect
                          options={models.map((m) => ({
                            value: String(m.id),
                            label: m.name,
                          }))}
                          value={
                            productData.model_id
                              ? {
                                value: String(productData.model_id),
                                label:
                                  modelLabels[String(productData.model_id)] ||
                                  models.find(
                                    (m) =>
                                      String(m.id) ===
                                      String(productData.model_id),
                                  )?.name ||
                                  "Select Model",
                              }
                              : null
                          }
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              model_id: opt?.value || "",
                            })
                          }
                          onInputChange={(value) => setModelSearchInput(value)}
                          isLoading={isModelsFetching}
                          filterOption={() => true}
                          placeholder={
                            productData.brand_id
                              ? "Search types/models..."
                              : "Pick a brand first"
                          }
                          isDisabled={!productData.brand_id}
                          isClearable
                        />
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="font-bold">Unit</Label>
                          {hasPermission("unit create") && (
                            <Button
                              variant="link"
                              className="p-0 h-auto text-xs font-bold text-primary hover:text-primary/80"
                              onClick={() => setShowUnitModal(true)}
                            >
                              + New Unit
                            </Button>
                          )}
                        </div>
                        <ReactSelect
                          options={units.map((u) => ({
                            value: String(u.id),
                            label: u.name,
                          }))}
                          value={
                            productData.unit_id
                              ? {
                                value: String(productData.unit_id),
                                label:
                                  unitLabels[String(productData.unit_id)] ||
                                  units.find(
                                    (u) =>
                                      String(u.id) ===
                                      String(productData.unit_id),
                                  )?.name ||
                                  "Select Unit",
                              }
                              : null
                          }
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              unit_id: opt?.value || "",
                            })
                          }
                          onInputChange={(value) => setUnitSearchInput(value)}
                          isLoading={isUnitsFetching}
                          filterOption={() => true}
                          placeholder="e.g. Pieces, Boxes..."
                          isClearable
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Pricing Section */}
            <section ref={pricingRef} id="pricing">
              <Card className="shadow-lg border-border/60 overflow-hidden bg-card">
                <div className="h-1 bg-primary" />
                <CardHeader className="bg-muted/50 pb-6 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100/10 text-emerald-600 dark:text-emerald-400 rounded-lg">
                      <DollarSign className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle>Pricing & Tax</CardTitle>
                      <CardDescription>
                        Setup cost, markup, and tax configurations
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-8 space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {hasPermission("product view_cost_price") && (
                      <div className="space-y-2">
                        <Label>
                          Cost Price ({currencySymbol}) *
                        </Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="number"
                            step="0.01"
                            className="pl-9 bg-muted/30"
                            value={productData.cost}
                            onChange={(e) => handleCostChange(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Markup (%)</Label>
                        {costPriceNum > 0 && sellPriceNum > 0 && (
                          <span className="text-[11px] text-muted-foreground font-medium">
                            Profit: {currencySymbol}
                            {(sellPriceNum - costPriceNum).toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div className="relative">
                        <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          className="pl-9 bg-muted/30"
                          value={markupPercent}
                          onChange={(e) => handleMarkupChange(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>
                        Selling Price ({currencySymbol}) *
                      </Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="number"
                          step="0.01"
                          className="pl-9 bg-muted/30"
                          value={productData.sell_price}
                          onChange={(e) => handleSellingPriceChange(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Auto-Sync Min/Max Price Checkbox & Fields */}
                  <div className="p-4 bg-muted/20 rounded-xl border border-border/60 space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sync-min-max-prices"
                        checked={useSamePriceForMinMax}
                        onCheckedChange={handleToggleSamePriceForMinMax}
                      />
                      <Label
                        htmlFor="sync-min-max-prices"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Use selling price as minimum and maximum selling price
                      </Label>
                    </div>

                    {!useSamePriceForMinMax && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 animate-in fade-in duration-200">
                        <div className="space-y-2">
                          <Label>Min Selling Price ({currencySymbol})</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="bg-muted/30"
                            value={productData.sell_price_min}
                            onChange={(e) =>
                              setProductData({
                                ...productData,
                                sell_price_min: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Max Selling Price ({currencySymbol})</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="bg-muted/30"
                            value={productData.sell_price_max}
                            onChange={(e) =>
                              setProductData({
                                ...productData,
                                sell_price_max: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 p-6 bg-emerald-500/5 rounded-2xl border border-emerald-500/10">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3.5 bg-background/60 rounded-xl border border-emerald-500/10">
                        <div className="space-y-0.5">
                          <Label className="text-sm font-semibold">
                            Is Taxable
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Enable if this product is subject to sales tax
                          </p>
                        </div>
                        <Switch
                          checked={productData.is_taxable}
                          onCheckedChange={(checked) =>
                            setProductData({
                              ...productData,
                              is_taxable: checked,
                            })
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">
                          Tax Method
                        </Label>
                        <ReactSelect
                          options={taxMethodOptions}
                          value={taxMethodOptions.find(
                            (opt) => opt.value === productData.tax_method,
                          )}
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              tax_method: opt.value,
                            })
                          }
                          isDisabled={!productData.is_taxable}
                          placeholder="Select Method"
                        />
                        <p className="text-[11px] text-muted-foreground italic">
                          Exclusive: Tax added on top of selling price. Inclusive: Selling price already includes tax.
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col justify-between p-4 bg-background/60 rounded-xl border border-emerald-500/10 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Store Tax Rate
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {currentStore?.name ? currentStore.name : "Active Store"}
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-xs font-semibold px-2.5 py-1",
                            productData.is_taxable && storeChargesTax
                              ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                              : "bg-muted text-muted-foreground border-border",
                          )}
                        >
                          {productData.is_taxable && storeChargesTax
                            ? `${storeTaxPercentage}% Tax Rate`
                            : productData.is_taxable
                              ? "0% (Store Tax-Exempt)"
                              : "Product Tax-Exempt"}
                        </Badge>
                      </div>

                      <div className="pt-3 border-t border-border/50 space-y-2">
                        {productData.is_taxable && storeChargesTax ? (
                          <>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">
                                {productData.tax_method === "inclusive"
                                  ? `Included Tax (${storeTaxPercentage}%):`
                                  : `Added Tax (${storeTaxPercentage}%):`}
                              </span>
                              <span className="font-semibold text-foreground">
                                {currencySymbol}{taxAmount.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-xs font-medium">
                              <span className="text-muted-foreground">
                                {productData.tax_method === "inclusive"
                                  ? "Pre-tax Price:"
                                  : "Total Customer Price:"}
                              </span>
                              <span className="text-sm font-bold text-emerald-600">
                                {currencySymbol}
                                {productData.tax_method === "inclusive"
                                  ? (sellPriceNum - taxAmount).toFixed(2)
                                  : finalPriceWithTax.toFixed(2)}
                              </span>
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-muted-foreground leading-relaxed italic">
                            {!productData.is_taxable
                              ? "Product is marked as tax-exempt. No tax will be applied at checkout."
                              : "Current store has 0% tax or no tax class configured in store settings."}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>



            {/* Inventory Section */}
            <section ref={inventoryRef} id="inventory">
              <Card className="shadow-lg border-border/60 overflow-hidden bg-card">
                <div className="h-1 bg-primary" />
                <CardHeader className="bg-muted/50 pb-6 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 text-blue-500 rounded-lg">
                      <Layers className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle>Inventory & Parameters</CardTitle>
                      <CardDescription>
                        Manage stock levels, alerts and serial numbers
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-8 space-y-8">
                  <div className="flex flex-col sm:flex-row gap-8 py-2">
                    <div className="flex-1 space-y-4 p-5 rounded-2xl bg-muted/30 border border-border">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <Label className="text-base font-bold text-foreground">
                              Manage Inventory
                            </Label>
                            <Tooltip side="top">
                              <TooltipTrigger asChild>
                                <Info className="h-3.5 w-3.5 text-blue-500 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">
                                  Automatically track and update stock levels on
                                  sales
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <p className="text-xs text-muted-foreground font-medium leading-relaxed">
                            System will automatically track and update
                            quantities
                          </p>
                        </div>
                        <Switch
                          checked={productData.manage_inventory}
                          onCheckedChange={(c) =>
                            setProductData({
                              ...productData,
                              manage_inventory: c,
                            })
                          }
                        />
                      </div>
                      {productData.manage_inventory && (
                        <div className="pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                            Low Stock Alert
                          </Label>
                          <Input
                            type="number"
                            value={productData.alert_quantity}
                            onChange={(e) =>
                              setProductData({
                                ...productData,
                                alert_quantity: e.target.value,
                              })
                            }
                            className="mt-2 bg-background border-border focus:ring-primary"
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 space-y-4 p-5 rounded-2xl bg-muted/30 border border-border">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <Label className="text-base font-bold text-foreground">
                              Tracking Type
                            </Label>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed italic">
                            How individual stock items are identified
                          </p>
                        </div>
                        <ReactSelect
                          options={trackingTypeOptions}
                          value={trackingTypeOptions.find(
                            (opt) =>
                              opt.value ===
                              (productData.tracking_type || "none"),
                          )}
                          onChange={(opt) =>
                            setProductData({
                              ...productData,
                              tracking_type: opt.value,
                              is_serialized: opt.value === "serial",
                            })
                          }
                          className="w-[180px]"
                          placeholder="Select type"
                        />
                      </div>
                    </div>
                  </div>

                  {IS_SUPERMARKET && (
                    <div className="p-6 bg-muted/30 rounded-2xl border border-border">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className="bg-amber-500/10 p-2 rounded-lg">
                            <AlertCircle className="h-5 w-5 text-amber-500" />
                          </div>
                          <div>
                            <h3 className="font-bold text-foreground">
                              Expiration Tracking
                            </h3>
                            <p className="text-[11px] text-muted-foreground italic">
                              Track expiry dates and automatic discount tiers
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={productData.is_expirable}
                          onCheckedChange={(c) =>
                            setProductData({ ...productData, is_expirable: c })
                          }
                        />
                      </div>

                      {productData.is_expirable && (
                        <div className="space-y-4 animate-in zoom-in-95 duration-300 border-t border-border/50 pt-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-bold text-foreground">
                              Discount Tiers Before Expiry
                            </Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setProductData({
                                  ...productData,
                                  expiry_discount_tiers: [
                                    ...(productData.expiry_discount_tiers ||
                                      []),
                                    { days_before: 1, discount_percent: 0 },
                                  ],
                                });
                              }}
                            >
                              <Plus className="h-4 w-4 mr-1" /> Add Tier
                            </Button>
                          </div>

                          {(productData.expiry_discount_tiers || []).map(
                            (tier, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-4"
                              >
                                <div className="flex-1 space-y-1">
                                  <Label className="text-xs">Days Before</Label>
                                  <Input
                                    type="number"
                                    min={1}
                                    value={tier.days_before}
                                    onChange={(e) => {
                                      const tiers = [
                                        ...(productData.expiry_discount_tiers ||
                                          []),
                                      ];
                                      tiers[idx].days_before =
                                        parseInt(e.target.value) || 1;
                                      setProductData({
                                        ...productData,
                                        expiry_discount_tiers: tiers,
                                      });
                                    }}
                                  />
                                </div>
                                <div className="flex-1 space-y-1">
                                  <Label className="text-xs">Discount %</Label>
                                  <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={tier.discount_percent}
                                    onChange={(e) => {
                                      const tiers = [
                                        ...(productData.expiry_discount_tiers ||
                                          []),
                                      ];
                                      tiers[idx].discount_percent =
                                        parseFloat(e.target.value) || 0;
                                      setProductData({
                                        ...productData,
                                        expiry_discount_tiers: tiers,
                                      });
                                    }}
                                  />
                                </div>
                                <div className="pt-5">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="text-muted-foreground hover:text-destructive"
                                    onClick={() => {
                                      const tiers = (
                                        productData.expiry_discount_tiers || []
                                      ).filter((_, i) => i !== idx);
                                      setProductData({
                                        ...productData,
                                        expiry_discount_tiers: tiers,
                                      });
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {productData.manage_inventory && (
                    <div className="p-6 bg-muted/30 rounded-2xl border border-border">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className="bg-primary p-2 rounded-lg">
                            <Plus className="h-5 w-5 text-primary-foreground" />
                          </div>
                          <div>
                            <h3 className="font-bold text-foreground">
                              Opening Stock
                            </h3>
                            <p className="text-[11px] text-muted-foreground italic">
                              Optional: Set starting stock for specific stores
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={openingStockEnabled}
                          onCheckedChange={setOpeningStockEnabled}
                        />
                      </div>

                      {openingStockEnabled && (
                        <div className="space-y-4 animate-in zoom-in-95 duration-300">
                          <div className="flex flex-col gap-3">
                            {openingStockData.map((row, idx) => (
                              <div
                                key={idx}
                                className="flex flex-wrap gap-3 items-center p-3 bg-card rounded-xl border border-border shadow-sm"
                              >
                                <div className="flex-1 min-w-[180px]">
                                  <ReactSelect
                                    options={stores
                                      .filter(
                                        (s) =>
                                          !openingStockData.some(
                                            (row, i) =>
                                              i !== idx &&
                                              String(row.store_id) ===
                                              String(s.id),
                                          ),
                                      )
                                      .map((s) => ({
                                        value: String(s.id),
                                        label: s.name,
                                      }))}
                                    value={
                                      row.store_id
                                        ? {
                                          value: row.store_id,
                                          label:
                                            stores.find(
                                              (s) =>
                                                String(s.id) === row.store_id,
                                            )?.name || "Select Store",
                                        }
                                        : null
                                    }
                                    onChange={(opt) => {
                                      const n = [...openingStockData];
                                      n[idx].store_id = opt.value;
                                      setOpeningStockData(n);
                                    }}
                                    placeholder="Select Store"
                                  />
                                </div>
                                <Input
                                  type="number"
                                  className="w-24 border-border h-10 bg-muted/30"
                                  value={row.opening_quantity}
                                  min={0}
                                  onKeyDown={(e) => {
                                    if (e.key === "-" || e.key === "e" || e.key === "E") {
                                      e.preventDefault();
                                    }
                                  }}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    const val = raw === "" ? "" : Math.max(0, parseInt(raw) || 0);
                                    const n = [...openingStockData];
                                    n[idx].opening_quantity = val;
                                    setOpeningStockData(n);
                                  }}
                                  placeholder="Qty"
                                />
                                <Input
                                  className="flex-1 min-w-[150px] bg-muted/30 border-border h-10"
                                  value={row.notes}
                                  onChange={(e) => {
                                    const n = [...openingStockData];
                                    n[idx].notes = e.target.value;
                                    setOpeningStockData(n);
                                  }}
                                  placeholder="Notes/Reason"
                                />
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  className="text-muted-foreground hover:text-destructive transition-colors"
                                  onClick={() =>
                                    setOpeningStockData(
                                      openingStockData.filter(
                                        (_, i) => i !== idx,
                                      ),
                                    )
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>

                                {productData.tracking_type === "serial" && (
                                  <div className="w-full mt-2 pt-2 border-t border-border/50">
                                    <div className="flex items-center justify-between mb-1.5">
                                      <Label className="text-[10px] uppercase tracking-wider block font-semibold text-muted-foreground">
                                        Serial Numbers
                                      </Label>
                                      {(() => {
                                        const targetQty =
                                          parseInt(row.opening_quantity) || 0;
                                        const serials = (
                                          row.serial_numbers || ""
                                        )
                                          .split(/[\n,]/)
                                          .map((s) => s.trim())
                                          .filter(Boolean);
                                        const count = serials.length;
                                        const isMatched =
                                          targetQty > 0 && count === targetQty;
                                        const isOver =
                                          targetQty > 0 && count > targetQty;
                                        return (
                                          <span
                                            className={`text-[10px] font-semibold ${isMatched
                                              ? "text-green-600 dark:text-green-400"
                                              : isOver
                                                ? "text-destructive"
                                                : "text-muted-foreground"
                                              }`}
                                          >
                                            {count} / {targetQty} entered
                                          </span>
                                        );
                                      })()}
                                    </div>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      onClick={() => openSerialModal(idx)}
                                      className={`w-full justify-between h-9 px-3 text-xs font-medium border ${parseInt(row.opening_quantity) > 0 &&
                                        (row.serial_numbers || "")
                                          .split(/[\n,]/)
                                          .map((s) => s.trim())
                                          .filter(Boolean).length ===
                                        parseInt(row.opening_quantity)
                                        ? "border-green-500/50 bg-green-50/50 text-green-700 dark:bg-green-950/20 dark:text-green-400"
                                        : "border-border hover:bg-muted"
                                        }`}
                                    >
                                      <span className="flex items-center gap-2">
                                        <ScanBarcode className="h-4 w-4" />
                                        {(row.serial_numbers || "").trim() ===
                                          ""
                                          ? "Add / Generate Serials"
                                          : "Manage Serials"}
                                      </span>
                                      <Badge
                                        variant={
                                          parseInt(row.opening_quantity) > 0 &&
                                            (row.serial_numbers || "")
                                              .split(/[\n,]/)
                                              .map((s) => s.trim())
                                              .filter(Boolean).length ===
                                            parseInt(row.opening_quantity)
                                            ? "default"
                                            : "secondary"
                                        }
                                        className="text-[10px]"
                                      >
                                        {(row.serial_numbers || "")
                                          .split(/[\n,]/)
                                          .map((s) => s.trim())
                                          .filter(Boolean).length}{" "}
                                        /{" "}
                                        {parseInt(row.opening_quantity) || 0}
                                      </Badge>
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full h-12 bg-card border-input text-muted-foreground hover:bg-muted border-dashed"
                              onClick={() => {
                                const usedStoreIds = openingStockData.map((r) =>
                                  String(r.store_id),
                                );
                                const availableStore = stores.find(
                                  (s) => !usedStoreIds.includes(String(s.id)),
                                );

                                if (!availableStore) {
                                  toast.error(
                                    "All available stores already have opening stock records",
                                  );
                                  return;
                                }

                                setOpeningStockData([
                                  ...openingStockData,
                                  {
                                    store_id: String(availableStore.id),
                                    opening_quantity: 1,
                                    unit_cost: productData.cost || 0,
                                    reorder_level:
                                      productData.alert_quantity || 0,
                                    location: "",
                                    notes: "",
                                    serial_numbers: "",
                                  },
                                ]);
                              }}
                            >
                              <Plus className="mr-2 h-4 w-4" /> Add opening
                              stock record
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </section>

            {/* Media Section */}
            <section ref={mediaRef} id="media">
              <Card className="shadow-lg border-border/60 overflow-hidden">
                <CardHeader className="bg-muted/10 pb-6 border-b flex flex-row items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-muted text-muted-foreground rounded-lg">
                      <ImageIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle>Product Media</CardTitle>
                      <CardDescription>
                        Gallery assets for the product
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-8">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {selectedImagesDetails.map((m, index) => (
                      <div
                        key={m.id}
                        className="group relative aspect-square border-2 border-border rounded-2xl overflow-hidden bg-card hover:border-primary transition-all duration-300"
                      >
                        <img
                          src={m.url}
                          alt={`Product ${index}`}
                          className="w-full h-full object-contain p-2 transition duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-2">
                          <Button
                            variant="destructive"
                            size="icon"
                            className="h-8 w-8 rounded-full shadow-xl"
                            onClick={() => {
                              setSelectedImagesDetails((prev) =>
                                prev.filter((item) => item.id !== m.id),
                              );
                              setProductData((prev) => ({
                                ...prev,
                                images: (prev.images || []).filter(
                                  (id) => id !== m.id,
                                ),
                              }));
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="absolute bottom-2 right-2 bg-background/90 backdrop-blur-sm px-2 py-0.5 rounded text-[10px] font-bold text-muted-foreground shadow-sm border border-border">
                          {(m.size / 1024).toFixed(0)} KB
                        </div>
                      </div>
                    ))}

                    <div
                      className="group aspect-square border-2 border-dashed border-border rounded-2xl flex flex-col items-center justify-center text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all duration-300"
                      onClick={() => setIsMediaLibraryOpen(true)}
                    >
                      <div className="p-3 bg-muted text-muted-foreground rounded-full group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-500">
                        <Plus className="h-6 w-6" />
                      </div>
                      <span className="mt-2 text-xs font-bold text-muted-foreground uppercase tracking-widest">
                        Add Media
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ProductCategoryModal
        open={showCategoryModal}
        onOpenChange={(isOpen) => {
          setShowCategoryModal(isOpen);
          if (!isOpen) setCategoryModalInitialName("");
        }}
        initialCategoryName={categoryModalInitialName}
        categories={categories}
        isLoadingCategories={isCategoriesBusy}
        simpleMode
        onSuccess={(newCategory) => {
          if (newCategory?.id) {
            setSelectedCategoryIds((prev) =>
              prev.includes(newCategory.id) ? prev : [...prev, newCategory.id],
            );
          }
          setCategorySearchInput("");
          setCategoryModalInitialName("");
        }}
      />

      <Dialog open={showBrandModal} onOpenChange={setShowBrandModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Brand</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-6">
            <div className="space-y-2">
              <Label>Brand Name</Label>
              <Input
                placeholder="e.g. Sony, Apple"
                value={brandFormData.name}
                onChange={(e) =>
                  setBrandFormData({ ...brandFormData, name: e.target.value })
                }
              />
            </div>
            <Button
              className="w-full h-11"
              onClick={() => {
                if (brandFormData.name)
                  createBrandMutation.mutate(brandFormData);
              }}
              disabled={createBrandMutation.isPending}
            >
              {createBrandMutation.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                "Confirm Brand"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showUnitModal} onOpenChange={setShowUnitModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Unit</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-6">
            <div className="space-y-2">
              <Label>Unit Name</Label>
              <Input
                value={unitFormData.name}
                onChange={(e) =>
                  setUnitFormData({ ...unitFormData, name: e.target.value })
                }
                placeholder="e.g. Kilograms"
              />
            </div>
            <div className="space-y-2">
              <Label>Short Tag</Label>
              <Input
                value={unitFormData.short_name}
                onChange={(e) =>
                  setUnitFormData({
                    ...unitFormData,
                    short_name: e.target.value,
                  })
                }
                placeholder="e.g. Kg"
              />
            </div>
            <Button
              className="w-full h-11"
              onClick={() => {
                if (unitFormData.name) createUnitMutation.mutate(unitFormData);
              }}
              disabled={createUnitMutation.isPending}
            >
              {createUnitMutation.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                "Save Unit"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showModelModal} onOpenChange={setShowModelModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Add Model for{" "}
              {
                brands.find(
                  (b) => String(b.id) === String(productData.brand_id),
                )?.name
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-6">
            <div className="space-y-2">
              <Label>Model Name</Label>
              <Input
                value={modelFormData.name}
                onChange={(e) =>
                  setModelFormData({
                    ...modelFormData,
                    name: e.target.value,
                    brand_id: productData.brand_id,
                  })
                }
                placeholder="e.g. iPhone 15 Pro"
              />
            </div>
            <Button
              className="w-full h-11"
              onClick={() => {
                if (modelFormData.name)
                  createModelMutation.mutate(modelFormData);
              }}
              disabled={createModelMutation.isPending}
            >
              Save Model
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detailed Description Modal */}
      <Dialog open={showDescriptionModal} onOpenChange={setShowDescriptionModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Detailed Product Description
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-3 space-y-2">
            <p className="text-xs text-muted-foreground">
              Add rich technical specifications, features, formatting and visual content.
            </p>
            <DefaultEditor
              value={productData.description || ""}
              onChange={(e) =>
                setProductData({
                  ...productData,
                  description: e.target.value,
                })
              }
              className="min-h-[300px] rounded-xl border-border overflow-hidden"
            />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button type="button" onClick={() => setShowDescriptionModal(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <MediaLibraryComponent
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onSelect={(ids, details) => {
          const selectedDetails = Array.isArray(details) ? details : [details];
          const selectedIds = Array.isArray(ids) ? ids : [ids];

          setSelectedImagesDetails((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const uniqueNewDetails = selectedDetails.filter(
              (item) => !existingIds.has(item.id),
            );
            return [...prev, ...uniqueNewDetails];
          });

          setProductData((prev) => ({
            ...prev,
            images: [...new Set([...(prev.images || []), ...selectedIds])],
            image:
              (prev.images || []).length > 0 ? prev.images[0] : selectedIds[0],
          }));

          setIsMediaLibraryOpen(false);
        }}
        selectionMode="multiple"
      />

      {/* Universal Add Serials Modal */}
      <AddSerialsModal
        isOpen={serialModal.isOpen}
        onClose={() => setSerialModal((prev) => ({ ...prev, isOpen: false }))}
        onSave={handleSaveSerials}
        initialSerials={serialModal.initialSerials}
        quantity={serialModal.quantity}
        productName={productData.name || "Product"}
        excludeSerials={excludeSerials}
      />
    </div>
  );
};

export default AddProduct;
